import React, { useMemo, useState } from 'react';
import { Alert, Button, Checkbox, InputNumber, Select, Space, Table, Tag, message } from 'antd';
import {
  DiscountPeriod,
  DiscountRuleDto,
  FanTierDto,
  useSetRuleTiers,
} from '@shared/api/hooks/useDiscountRules';

interface CellState {
  enabled: boolean;
  maxRedemptionsPerUser?: number;
  period?: DiscountPeriod;
}

/**
 * Matrice Regola x Tier: risponde alla domanda "a cosa ha diritto un tifoso
 * ORO?" senza dover aprire una regola alla volta.
 *
 * Semantica importante e resa esplicita nella UI: una riga con NESSUN tier
 * selezionato vale per TUTTI i tier, non per nessuno.
 */
export function TierMatrix({
  rules,
  tiers,
}: {
  rules: DiscountRuleDto[];
  tiers: FanTierDto[];
}): React.ReactElement {
  const setTiers = useSetRuleTiers();
  const [draft, setDraft] = useState<Record<string, Record<string, CellState>>>({});

  const activeTiers = useMemo(() => tiers.filter((t) => t.isActive), [tiers]);

  const cell = (rule: DiscountRuleDto, tierId: string): CellState => {
    const d = draft[rule.id]?.[tierId];
    if (d) return d;
    const row = rule.tiers.find((t) => t.tierId === tierId);
    return {
      enabled: Boolean(row),
      maxRedemptionsPerUser: row?.maxRedemptionsPerUser ?? undefined,
      period: row?.period ?? undefined,
    };
  };

  const setCell = (ruleId: string, tierId: string, next: CellState): void => {
    setDraft((d) => ({ ...d, [ruleId]: { ...(d[ruleId] ?? {}), [tierId]: next } }));
  };

  const save = async (rule: DiscountRuleDto): Promise<void> => {
    const payload = activeTiers
      .map((t) => ({ tierId: t.id, ...cell(rule, t.id) }))
      .filter((c) => c.enabled)
      .map(({ tierId, maxRedemptionsPerUser, period }) => ({
        tierId,
        maxRedemptionsPerUser,
        period,
      }));
    try {
      await setTiers.mutateAsync({ id: rule.id, tiers: payload });
      setDraft((d) => ({ ...d, [rule.id]: {} }));
      message.success(
        payload.length === 0
          ? `"${rule.name}": ora vale per TUTTI i tier`
          : `"${rule.name}": ${payload.length} tier abilitati`,
      );
    } catch (e) {
      message.error(`Salvataggio fallito: ${String(e)}`);
    }
  };

  if (activeTiers.length === 0) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Nessun tier configurato"
        description="Crea prima i tier tifoso nella scheda Tier: senza tier, le regole valgono per tutti."
      />
    );
  }

  return (
    <>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Una regola senza nessun tier selezionato vale per TUTTI i tifosi."
        description="Seleziona dei tier solo se vuoi riservare la regola a quelli."
      />
      <Table
        size="small"
        rowKey="id"
        dataSource={rules}
        pagination={false}
        scroll={{ x: 'max-content' }}
        columns={[
          {
            title: 'Regola',
            dataIndex: 'name',
            fixed: 'left' as const,
            width: 220,
            render: (name: string, r: DiscountRuleDto) => (
              <Space direction="vertical" size={0}>
                <span>{name}</span>
                {r.tiers.length === 0 && <Tag color="blue">tutti i tier</Tag>}
              </Space>
            ),
          },
          ...activeTiers.map((t) => ({
            title: t.key,
            key: t.id,
            width: 180,
            render: (_: unknown, rule: DiscountRuleDto) => {
              const c = cell(rule, t.id);
              return (
                <Space direction="vertical" size={2}>
                  <Checkbox
                    checked={c.enabled}
                    onChange={(e) => setCell(rule.id, t.id, { ...c, enabled: e.target.checked })}
                  >
                    abilitato
                  </Checkbox>
                  {c.enabled && (
                    <Space size={4}>
                      <InputNumber
                        size="small"
                        min={1}
                        style={{ width: 70 }}
                        placeholder="max"
                        value={c.maxRedemptionsPerUser}
                        onChange={(v) =>
                          setCell(rule.id, t.id, { ...c, maxRedemptionsPerUser: v ?? undefined })
                        }
                      />
                      <Select
                        size="small"
                        style={{ width: 90 }}
                        allowClear
                        placeholder="periodo"
                        value={c.period}
                        onChange={(v) => setCell(rule.id, t.id, { ...c, period: v })}
                        options={[
                          { value: 'ALL_TIME', label: 'totale' },
                          { value: 'MONTH', label: 'mese' },
                          { value: 'SEASON', label: 'stagione' },
                        ]}
                      />
                    </Space>
                  )}
                </Space>
              );
            },
          })),
          {
            title: '',
            key: 'save',
            fixed: 'right' as const,
            width: 100,
            render: (_: unknown, rule: DiscountRuleDto) => (
              <Button
                size="small"
                type="primary"
                disabled={!draft[rule.id] || Object.keys(draft[rule.id]).length === 0}
                loading={setTiers.isPending}
                onClick={() => void save(rule)}
              >
                Salva
              </Button>
            ),
          },
        ]}
      />
    </>
  );
}
